import React from 'react';
import classNames from 'classnames';
import {
  APP_ACCENT,
  APP_BG,
  APP_BORDER,
} from '@/constant/theme/colors.constant';
import { IconBadge, type BadgeVariant } from '../../atoms/IconBadge';
import { Text } from '../../atoms/AppText';

interface SectionCardProps {
  /** Icon rendered inside the gradient badge in the card header */
  icon: React.ReactNode;
  /** Bold section title */
  title: string;
  /** Optional muted subtitle below the title */
  subtitle?: string;
  /** Optional count badge rendered on the right of the header */
  badge?: number;
  /** Optional arbitrary content rendered on the right of the header (replaces badge if both set) */
  headerRight?: React.ReactNode;
  /** Card body content */
  children: React.ReactNode;
  /**
   * Compact mode — tighter header padding and smaller title text.
   * Ideal for table-expand panels.
   */
  compact?: boolean;
  /**
   * Tailwind classes applied to the body wrapper.
   * Defaults to `"p-5"` — pass `""` for no padding (full-bleed content).
   */
  bodyClassName?: string;
  /** Extra Tailwind classes on the root card element */
  className?: string;
  /**
   * Override Tailwind classes for the header band background.
   * Defaults to `APP_BG.canvas`.
   */
  headerClassName?: string;
  /**
   * Override Tailwind classes for the body background.
   * Defaults to `APP_BG.base`.
   */
  bodyBg?: string;
  /**
   * Icon badge style. `"solid"` (default) = full gradient + white icon.
   * `"light"` = semi-transparent tinted bg + primary-colored icon.
   */
  iconVariant?: BadgeVariant;
  /** data-testid for automation */
  testId?: string;
}

/**
 * Reusable content card with a gray header band (icon + title + subtitle) and
 * a white body area.  Matches the design used across Profile and ViewProvider.
 *
 * Use `compact` for tighter expand-row contexts and `badge` to show a count.
 */
export const SectionCard = ({
  icon,
  title,
  subtitle,
  badge,
  headerRight,
  children,
  compact = false,
  bodyClassName,
  className = '',
  headerClassName,
  bodyBg,
  iconVariant = 'solid',
  testId,
}: SectionCardProps) => (
  <div
    data-testid={testId ?? ''}
    className={classNames(
      `flex flex-col rounded-2xl border ${APP_BORDER.default} overflow-hidden shadow-sm transition-colors duration-300`,
      className,
    )}
  >
    {/* ── Header band ── */}
    <div
      className={classNames(
        `flex flex-wrap items-start sm:items-center justify-between gap-3 border-b ${APP_BORDER.default}`,
        headerClassName ?? APP_BG.sectionHeader,
        compact ? 'px-4 py-2.5' : 'px-5 py-3.5',
      )}
    >
      <div className="flex items-center justify-start gap-3 min-w-0 flex-1">
        <IconBadge icon={icon} size="sm" variant={iconVariant} />
        <div className="min-w-0">
          <Text
            as="p"
            size={compact ? 'md' : 'base'}
            weight="bold"
            color={compact ? 'soft' : 'default'}
            tracking={compact ? 'wider' : 'normal'}
            leading="none"
            className="break-words"
          >
            {title}
          </Text>
          {subtitle && (
            <Text
              as="p"
              size="md"
              color="subtle"
              className="mt-0.5 break-words"
            >
              {subtitle}
            </Text>
          )}
        </div>
      </div>
      {headerRight ??
        (badge !== undefined && (
          <Text
            as="span"
            size="sm"
            weight="bold"
            color="primary"
            className={`px-2 py-0.5 rounded-full ${APP_ACCENT.primary.bg}/15 flex-shrink-0`}
          >
            {badge}
          </Text>
        ))}
    </div>

    {/* ── Body ── */}
    <div
      className={classNames(
        'flex-1 min-w-0',
        bodyBg ?? APP_BG.base,
        bodyClassName !== undefined ? bodyClassName : 'p-4 sm:p-5',
      )}
    >
      {children}
    </div>
  </div>
);
